import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  acknowledgeStep,
  completeStep,
  incompleteStep,
  toggleChecklistItem,
  type Program,
  type ProgramItem,
} from "@/api/program";
import { pickContent, pickTitle, type ContentType, type TitleMap } from "@/api/content";
import { downloadFile } from "@/api/download";
import { Badge, Button, Card } from "@/components";
import { toast } from "@/state/toast.store";
import { useLang } from "@/i18n/LanguageContext";
import { groupLabel, scheduleLabel } from "@/features/journeys/schedule";
import { ModuleContent } from "./content/ModuleContent";
import { ProgramFocus } from "./ProgramFocus";
import { QuizPanel } from "./QuizPanel";

export function ProgramView({ program, readOnly = false }: { program: Program; readOnly?: boolean }) {
  const qc = useQueryClient();
  const { t, lang } = useLang();
  const apply = { onSuccess: (p: Program) => qc.setQueryData(["my-program"], p) };

  const complete = useMutation({ mutationFn: completeStep, ...apply });
  const incomplete = useMutation({ mutationFn: incompleteStep, ...apply });
  const acknowledge = useMutation({ mutationFn: acknowledgeStep, ...apply });
  const checklist = useMutation({
    mutationFn: (v: { stepId: string; index: number; checked: boolean }) => toggleChecklistItem(v.stepId, v.index, v.checked),
    ...apply,
  });
  const busy = complete.isPending || incomplete.isPending || acknowledge.isPending;

  // Fully-completed journeys earn a downloadable certificate. (Hook must run
  // before the early return below.)
  const completedJourneys = useMemo(() => {
    const map = new Map<string, { id: string; name: string; name_i18n?: TitleMap; total: number; done: number }>();
    for (const it of program.items) {
      const g = map.get(it.journey_id) ?? { id: it.journey_id, name: it.journey_name, name_i18n: it.journey_name_i18n, total: 0, done: 0 };
      g.total += 1;
      if (it.completed) g.done += 1;
      map.set(it.journey_id, g);
    }
    return [...map.values()].filter((g) => g.total > 0 && g.done === g.total);
  }, [program.items]);

  if (program.items.length === 0) {
    return <p className="no-data-message">{t("Er is nog geen programma aan je toegewezen. Vraag je beheerder om je in te delen.", "No program has been assigned to you yet. Ask your administrator to enrol you.")}</p>;
  }

  // Items arrive sorted by offset; group them by month·week in that order.
  const groups: { label: string; items: ProgramItem[] }[] = [];
  for (const item of program.items) {
    const label = groupLabel(item.offset_days, t);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
  }
  const { completed, total, required_total, required_done, percent } = program.progress;

  async function downloadCertificate(journeyId: string) {
    const path = readOnly
      ? `/api/employees/${program.user_id}/certificates/${journeyId}.pdf`
      : `/api/me/certificates/${journeyId}.pdf`;
    try {
      await downloadFile(path, "certificate.pdf");
    } catch (e) {
      toast.err(e instanceof Error ? e.message : t("Download mislukt", "Download failed"));
    }
  }

  function ackControl(item: ProgramItem) {
    if (item.acknowledged_at) {
      return <Badge tone="ok">✓ {t("Akkoord op", "Signed off on")} {new Date(item.acknowledged_at).toLocaleDateString()}</Badge>;
    }
    return (
      <div className="ack-box">
        {item.needs_reack && <p className="ack-reack">{t("De inhoud is gewijzigd — opnieuw akkoord nodig.", "The content has changed — re-acknowledgement required.")}</p>}
        <label className="ack-check">
          <input type="checkbox" disabled={readOnly || busy} checked={false} onChange={() => acknowledge.mutate(item.step_id)} />
          {t("Ik heb dit gelezen en begrepen", "I have read and understood this")}
        </label>
      </div>
    );
  }

  function control(item: ProgramItem) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {item.requires_acknowledgement && ackControl(item)}
        {!item.requires_acknowledgement && !item.has_quiz && (
          <label className="ack-check">
            <input type="checkbox" disabled={readOnly || busy} checked={item.completed} onChange={(e) => (e.target.checked ? complete.mutate(item.step_id) : incomplete.mutate(item.step_id))} />
            {t("Afgerond", "Completed")}
          </label>
        )}
        {item.has_quiz && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Badge tone={item.quiz_passed ? "ok" : "warn"}>{item.quiz_passed ? t("Kennischeck: geslaagd", "Knowledge check: passed") : t("Kennischeck: nog te doen", "Knowledge check: to do")}</Badge>
            {!readOnly && <QuizPanel stepId={item.step_id} onSubmitted={() => qc.invalidateQueries({ queryKey: ["my-program"] })} />}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {!readOnly && <ProgramFocus program={program} />}
      <div className="progress-summary">
        <div className="progress-bar"><div className="progress-fill" style={{ width: `${percent}%` }} /></div>
        <span>
          <strong>{completed} / {total}</strong> {t("afgerond", "completed")}
          {required_total > 0 && ` · ${required_total - required_done} ${t("verplichte stap(pen) open", "required step(s) open")}`}
        </span>
      </div>

      {groups.map((g) => (
        <div key={g.label} style={{ marginBottom: 28 }}>
          <p className="eyebrow" style={{ marginBottom: 14 }}>{g.label}</p>
          {g.items.map((item) => (
            <Card key={item.step_id} className="program-item">
              <div className="program-item-head">
                <h3 style={{ fontSize: 20 }}>{pickTitle(item.title_i18n, lang, item.title)}</h3>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {item.completed && <Badge tone="ok">{t("afgerond", "completed")}</Badge>}
                  {!item.completed && item.required && <Badge tone="warn">{t("verplicht", "required")}</Badge>}
                  <Badge tone="neutral">{scheduleLabel(item.offset_days, t)}</Badge>
                  {item.available_on && <Badge tone="neutral">{t("vanaf", "from")} {new Date(item.available_on).toLocaleDateString()}</Badge>}
                </div>
              </div>
              <p style={{ color: "var(--grey-text)", fontSize: 12.5, fontFamily: "var(--mono)", marginBottom: 14 }}>{pickTitle(item.journey_name_i18n, lang, item.journey_name)}</p>
              <ModuleContent
                type={item.content_type as ContentType}
                content={pickContent(item.content, lang)}
                readOnly={readOnly}
                onChecklistToggle={(index, checked) => checklist.mutate({ stepId: item.step_id, index, checked })}
              />
              <div style={{ marginTop: 16, borderTop: "1px solid var(--line)", paddingTop: 14 }}>{control(item)}</div>
            </Card>
          ))}
        </div>
      ))}

      {completedJourneys.length > 0 && (
        <Card style={{ marginTop: 8 }}>
          <p className="card-num">{t("CERTIFICATEN", "CERTIFICATES")}</p>
          <h3 style={{ marginBottom: 12 }}>{t("Afgeronde trajecten", "Completed journeys")}</h3>
          <ul className="list-rows">
            {completedJourneys.map((j) => (
              <li key={j.id}>
                <span><Badge tone="ok">✓</Badge> {pickTitle(j.name_i18n, lang, j.name)}</span>
                <Button variant="outline" style={{ padding: "6px 12px", fontSize: 13 }} onClick={() => downloadCertificate(j.id)}>
                  {t("Download certificaat", "Download certificate")}
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
